import { CustomSelect } from '../../shared/components/CustomSelect';
import {
  ChevronDown,
  Copy,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CustomFieldsService from '../../services/customFields/customFieldsService';
import { useAuth } from '../../contexts/AuthContext';
import type { JiraField } from '../../shared/types';
import type { CustomFieldKind, ProductCustomField, ProductCustomFieldUpdate } from './types';
import { JiraFieldPicker } from '../../shared/components/JiraFieldPicker';
import { apiFetch } from '../../shared/apiClient';
import { LivePreview } from './LivePreview';

const KIND_OPTIONS: Array<{ value: CustomFieldKind; label: string; hint: string }> = [
  { value: 'string', label: 'String', hint: 'Plain text (e.g. team name)' },
  { value: 'option', label: 'Option', hint: 'Single-select dropdown' },
  { value: 'array', label: 'Array', hint: 'Multi-select / multi-value' },
  { value: 'user', label: 'User', hint: 'Assignee-like user picker' },
  { value: 'number', label: 'Number', hint: 'Numeric value' },
  { value: 'cascading', label: 'Cascading', hint: 'Parent / child select' },
];

/**
 * RLX-FEAT-004 — Release classifier field picker. Chooses which Jira field maps
 * a story to its release (default `fixVersion`, e.g. `targetVersion`). Used by
 * Release distribution, Sprint distribution, and the Release Roadmap so all
 * three agree. Persisted via PUT /api/forecast/release-field.
 */
const ReleaseFieldRow: React.FC<{ productName: string | null }> = ({ productName }) => {
  const [value, setValue] = useState<string>('fixVersion');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Row-level "Apply to all" state — transient success line, same style as error.
  const [applying, setApplying] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [registered, setRegistered] = useState<
    { id: number; jiraFieldId: string; displayName: string; fieldKind: string }[]
  >([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fieldRes, catalog] = await Promise.all([
        apiFetch('/api/forecast/release-field'),
        CustomFieldsService.getCatalog(),
      ]);
      if (fieldRes.ok) {
        const data = await fieldRes.json();
        setValue(data.jiraFieldId || 'fixVersion');
      }
      setRegistered(
        (catalog || []).map((c) => ({
          id: c.id,
          jiraFieldId: c.jiraFieldId,
          displayName: c.displayName,
          fieldKind: c.fieldKind,
        }))
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load release field setting');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onChange = async (jiraFieldId: string) => {
    if (!jiraFieldId) return;
    setValue(jiraFieldId);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await apiFetch('/api/forecast/release-field', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jiraFieldId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save release field setting');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Copy this product's release classifier field to every other product via
   * the shared project-settings apply-to-all endpoint (jiraReleaseFieldId is
   * whitelisted server-side).
   */
  const handleApplyToAll = async () => {
    if (
      !window.confirm(
        `Copy release classifier field "${value}" to every product in the organization?\n\n` +
          `This overwrites the per-product release field setting of every other product.`
      )
    ) {
      return;
    }
    setApplying(true);
    setError(null);
    setNotice(null);
    try {
      const res = await apiFetch('/api/project-settings/apply-to-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys: ['jiraReleaseFieldId'] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to apply to all products');
      }
      const data = await res.json();
      setNotice(`Applied "${value}" to ${data.appliedTo} product(s).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply to all products');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '12px 14px',
        border: '1px solid var(--color-border, #2a2a2a)',
        borderRadius: 8,
        background: 'var(--color-bg, #111)',
        borderTop: '3px solid var(--color-accent, #6366f1)',
        marginBottom: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
        <strong style={{ fontSize: 13 }}>Release classifier field</strong>
        <span style={{ fontSize: 11, color: 'var(--color-text-secondary, #94a3b8)' }}>
          Which Jira field maps a story to its release
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 260 }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--color-text-secondary, #94a3b8)',
            }}
          >
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        ) : (
          <JiraFieldPicker
            value={value}
            onChange={onChange}
            registeredFields={registered}
            allowAdd
            placeholder="Select the release classifier field…"
            description={
              saving ? (
                'Saving…'
              ) : (
                <span>
                  Used by <strong>Release distribution</strong>,{' '}
                  <strong>Sprint distribution</strong> and the <strong>Release Roadmap</strong> so
                  all three agree on which stories belong to which release.
                  {productName ? ` Applies to “${productName}”.` : ''} Default:{' '}
                  <code>fixVersion</code>.
                </span>
              )
            }
            onRegistered={(entry) =>
              setRegistered((prev) =>
                prev.some((p) => p.jiraFieldId === entry.jiraFieldId) ? prev : prev.concat(entry)
              )
            }
          />
        )}
        {error ? <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>{error}</div> : null}
        {notice ? (
          <div style={{ marginTop: 6, fontSize: 12, color: '#86efac' }}>{notice}</div>
        ) : null}
        {!loading && productName ? (
          <button
            type="button"
            onClick={handleApplyToAll}
            disabled={saving || applying}
            title={`Copy release classifier field "${value}" to every product in the organization`}
            style={{
              marginTop: 8,
              background: 'transparent',
              border: '1px solid var(--color-border, #555)',
              color: 'var(--color-text-secondary, #aaa)',
              cursor: saving || applying ? 'not-allowed' : 'pointer',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              opacity: saving || applying ? 0.6 : 1,
            }}
          >
            {applying ? <Loader2 className="animate-spin" size={12} /> : <Copy size={12} />}
            Apply to all
          </button>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Project Settings section: register Jira custom fields once at the org
 * level, then per-product toggle where each one appears in Forecast
 * (filters / table column / summary breakdown). Mirrors the
 * `CommitmentSourceConfigSection` visual language.
 */
export const ForecastCustomFieldsSection: React.FC = () => {
  const { currentProduct } = useAuth();
  const productName = currentProduct?.name?.trim() || null;

  const [fields, setFields] = useState<ProductCustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [jiraFields, setJiraFields] = useState<JiraField[]>([]);
  const [loadingJiraFields, setLoadingJiraFields] = useState(false);
  const [selectedField, setSelectedField] = useState<JiraField | null>(null);
  const [pickedKind, setPickedKind] = useState<CustomFieldKind>('string');
  const [fieldSearch, setFieldSearch] = useState('');
  const [fieldDropdownOpen, setFieldDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /**
   * True while the LivePreview is fetching samples from Jira. Used to disable
   * the Register button so the user cannot register before the recommendation
   * lands (and accidentally pick the wrong kind).
   */
  const [previewLoading, setPreviewLoading] = useState(false);

  // Per-row sync state
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  // Per-row "Copy to all products" state
  const [applyingId, setApplyingId] = useState<number | null>(null);

  /**
   * DatabaseUpdater-style live log so the user gets the same level of
   * feedback they get on Analytics → Database (see DOM screenshot the user
   * provided): timestamped lines, percent bar, and an explicit success/fail
   * marker at the end. We keep the array bounded to avoid unbounded growth
   * across multiple Sync runs.
   */
  const MAX_LOG_LINES = 500;
  const [syncLogs, setSyncLogs] = useState<string[]>([]);
  const [syncProgress, setSyncProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [syncOutcome, setSyncOutcome] = useState<'ok' | 'partial' | 'error' | null>(null);
  const logsContainerRef = useRef<HTMLDivElement | null>(null);

  const appendLog = useCallback((line: string) => {
    const ts = new Date().toLocaleTimeString();
    setSyncLogs((prev) => {
      const next = prev.concat(`[${ts}] ${line}`);
      // Trim the head; only keep the tail so memory stays bounded but the
      // user always sees the most recent activity (which is what the
      // logs-container scroll-to-bottom effect below will reveal anyway).
      return next.length > MAX_LOG_LINES ? next.slice(-MAX_LOG_LINES) : next;
    });
  }, []);

  // Auto-scroll the log to the bottom as new lines come in. Mirrors the
  // behaviour of DatabaseUpdater's logs-container — without this, the user
  // sees the first few lines and has to manually scroll.
  useEffect(() => {
    const el = logsContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [syncLogs]);

  /**
   * Re-key on the active product so a global product switch (top-bar
   * switcher) automatically reloads per-product activations. Without
   * `currentProduct?.id` in the deps, the page would keep showing the
   * previous product's toggles after a switch.
   */
  const fetchFields = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CustomFieldsService.getProductSettings();
      setFields(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load custom fields');
    } finally {
      setLoading(false);
    }
    // currentProduct?.id is in the deps on purpose: it changes the callback
    // identity so the useEffect below re-runs on product switch. The body
    // doesn't use it directly because apiFetch reads the X-Product-Id header
    // automatically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct?.id]);

  useEffect(() => {
    void fetchFields();
  }, [fetchFields]);

  /**
   * On product switch, drop any in-flight sync row state and stale toast so
   * the new product's table starts clean (no "Syncing Cluster…" hanging
   * around when Cluster might not even be activated for the new product).
   */
  useEffect(() => {
    setSyncingId(null);
    setSyncMessage(null);
    setShowAddForm(false);
    setSelectedField(null);
    setSyncLogs([]);
    setSyncProgress(null);
    setSyncOutcome(null);
  }, [currentProduct?.id]);

  const handleToggle = async (cfg: ProductCustomField, patch: ProductCustomFieldUpdate) => {
    try {
      const updated = await CustomFieldsService.updateProductSetting(cfg.orgFieldId, {
        inFilters: cfg.inFilters,
        inTable: cfg.inTable,
        inSummary: cfg.inSummary,
        inDistribution: cfg.inDistribution,
        sortOrder: cfg.sortOrder,
        ...patch,
      });
      setFields((prev) => prev.map((f) => (f.orgFieldId === updated.orgFieldId ? updated : f)));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle field');
    }
  };

  const handleDelete = async (cfg: ProductCustomField) => {
    if (
      !window.confirm(
        `Remove "${cfg.displayName}" from the catalog? This unregisters it for the whole organization and clears its values from Forecast for every product.`
      )
    ) {
      return;
    }
    try {
      await CustomFieldsService.removeCatalogEntry(cfg.orgFieldId);
      setFields((prev) => prev.filter((f) => f.orgFieldId !== cfg.orgFieldId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove field');
    }
  };

  const handleSync = async (cfg: ProductCustomField) => {
    setSyncingId(cfg.orgFieldId);
    setSyncMessage(`Syncing ${cfg.displayName}…`);
    setSyncLogs([]);
    setSyncProgress(null);
    setSyncOutcome(null);
    appendLog(
      `🚀 Sync now: pulling current Jira values for "${cfg.displayName}" (${cfg.jiraFieldId}) for every task in this product…`
    );
    appendLog('⏳ Progress will update every batch. Do not close the page.');
    try {
      const out = await CustomFieldsService.syncFields([cfg.jiraFieldId], (evt) => {
        if (evt.type === 'progress') {
          const current = evt.current ?? 0;
          const total = evt.total ?? 0;
          setSyncProgress({ current, total });
          // Prefer the server's human message (e.g. "Fetching batch 2 of 4
          // (issues 26-50)…" or "Syncing 1 custom field(s) for 83 tasks...")
          // over the bare "▶ N/M" — it's a lot more informative when the
          // user is staring at a slow Jira fetch.
          if (evt.message) {
            appendLog(`▶ ${evt.message}`);
            setSyncMessage(evt.message);
          } else {
            const updatedPart = typeof evt.updated === 'number' ? `, ${evt.updated} touched` : '';
            const failedPart =
              typeof evt.failed === 'number' && evt.failed > 0 ? `, ${evt.failed} failed` : '';
            appendLog(`▶ ${current}/${total}${updatedPart}${failedPart}`);
          }
        }
      });
      const finalLine = out.message || `Done. ${out.updated}/${out.total} updated.`;
      const isPartial = out.failed > 0;
      appendLog(isPartial ? `⚠ ${finalLine}` : `✅ ${finalLine}`);
      setSyncMessage(finalLine);
      setSyncOutcome(isPartial ? 'partial' : 'ok');
      setSyncProgress({ current: out.total, total: out.total });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      appendLog(`❌ ${msg}`);
      setSyncMessage(msg);
      setSyncOutcome('error');
    } finally {
      setSyncingId(null);
    }
  };

  const handleApplyToAll = async (cfg: ProductCustomField) => {
    const summary =
      `In filters: ${cfg.inFilters ? 'on' : 'off'}, ` +
      `In table: ${cfg.inTable ? 'on' : 'off'}, ` +
      `In summary: ${cfg.inSummary ? 'on' : 'off'}, ` +
      `In distribution: ${cfg.inDistribution ? 'on' : 'off'}`;
    if (
      !window.confirm(
        `Copy "${cfg.displayName}" toggles to every product in the organization?\n\n` +
          `Current toggles for the active product:\n${summary}\n\n` +
          `This overwrites any existing per-product settings for this field.`
      )
    ) {
      return;
    }
    setApplyingId(cfg.orgFieldId);
    setSyncMessage(`Applying "${cfg.displayName}" to all products…`);
    setSyncLogs([]);
    setSyncProgress(null);
    setSyncOutcome(null);
    appendLog(`🚀 Applying "${cfg.displayName}" toggles to every product…`);
    appendLog(`   ${summary}`);
    try {
      const out = await CustomFieldsService.applyToAllProducts(cfg.orgFieldId, {
        inFilters: cfg.inFilters,
        inTable: cfg.inTable,
        inSummary: cfg.inSummary,
        inDistribution: cfg.inDistribution,
      });
      const msg = `Applied "${cfg.displayName}" to ${out.appliedTo} product(s).`;
      appendLog(`✅ ${msg}`);
      setSyncMessage(msg);
      setSyncOutcome('ok');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Apply-to-all failed';
      appendLog(`❌ ${msg}`);
      setSyncMessage(msg);
      setSyncOutcome('error');
    } finally {
      setApplyingId(null);
    }
  };

  const handleOpenAddForm = async () => {
    setShowAddForm(true);
    setSelectedField(null);
    setFieldSearch('');
    setPickedKind('string');
    if (jiraFields.length === 0) {
      setLoadingJiraFields(true);
      try {
        const list = await CustomFieldsService.getJiraFields();
        setJiraFields(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch Jira fields');
      } finally {
        setLoadingJiraFields(false);
      }
    }
  };

  const handleAddField = async () => {
    if (!selectedField) return;
    setSaving(true);
    setError(null);
    try {
      await CustomFieldsService.addCatalogEntry({
        jiraFieldId: selectedField.id,
        displayName: selectedField.name,
        fieldKind: pickedKind,
      });
      await fetchFields();
      setShowAddForm(false);
      setSelectedField(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add field');
    } finally {
      setSaving(false);
    }
  };

  const filteredJiraFields = jiraFields.filter((f) => {
    const existing = new Set(fields.map((c) => c.jiraFieldId));
    if (existing.has(f.id)) return false;
    if (!fieldSearch.trim()) return true;
    const q = fieldSearch.toLowerCase();
    return f.name.toLowerCase().includes(q) || f.id.toLowerCase().includes(q);
  });

  const renderToggle = (on: boolean, onChange: () => void, ariaLabel: string) => (
    <label
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 36,
        height: 20,
        cursor: 'pointer',
      }}
      aria-label={ariaLabel}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={onChange}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: on ? 'var(--color-accent, #6366f1)' : 'var(--color-bg-elevated, #333)',
          borderRadius: 10,
          transition: 'background 0.2s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: on ? 18 : 2,
            top: 2,
            width: 16,
            height: 16,
            background: '#fff',
            borderRadius: '50%',
            transition: 'left 0.2s',
          }}
        />
      </span>
    </label>
  );

  if (loading) {
    return (
      <div className="card-section">
        <h2 className="section-title">
          <Settings className="label-icon" />
          Forecast custom fields
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
          <Loader2 className="animate-spin" size={16} />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="card-section">
      <h2
        className="section-title"
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}
      >
        <Settings className="label-icon" />
        <span>Forecast custom fields</span>
        {productName ? (
          <span
            title="Per-product toggles below apply to this product. Switch product in the top bar to configure another."
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 10px',
              borderRadius: 999,
              background: 'rgba(99,102,241,0.12)',
              border: '1px solid rgba(99,102,241,0.4)',
              color: 'var(--color-accent, #6366f1)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >
            Active product: {productName}
          </span>
        ) : null}
      </h2>
      <p className="section-description" style={{ marginBottom: 20 }}>
        The <strong>catalog</strong> (which Jira fields exist) is shared across the whole
        organization. The <strong>toggles below</strong> (In filters / In table / In summary) apply
        only to <strong>{productName ? `“${productName}”` : 'the active product'}</strong>; switch
        product in the top bar to configure another. Values sync automatically on every Full /
        Incremental Update — use <strong>Sync now</strong> for an on-demand backfill of just one
        field.
      </p>

      {/* RLX-FEAT-004: release-classifier field — used by Release + Sprint distribution and the Roadmap. */}
      <ReleaseFieldRow productName={productName} />

      {!productName ? (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(245,158,11,0.12)',
            color: '#f59e0b',
            fontSize: 13,
            marginBottom: 12,
          }}
          role="status"
        >
          No product selected. Pick a product in the top bar to manage its per-product toggles
          below.
        </div>
      ) : null}

      {error && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(239,68,68,0.15)',
            color: '#ef4444',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            dismiss
          </button>
        </div>
      )}

      {syncMessage && (
        <div
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            background:
              syncOutcome === 'error'
                ? 'rgba(239,68,68,0.12)'
                : syncOutcome === 'partial'
                  ? 'rgba(245,158,11,0.12)'
                  : 'rgba(99,102,241,0.12)',
            color:
              syncOutcome === 'error'
                ? '#ef4444'
                : syncOutcome === 'partial'
                  ? '#f59e0b'
                  : 'var(--color-accent, #6366f1)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {syncMessage}
          <button
            onClick={() => {
              setSyncMessage(null);
              setSyncLogs([]);
              setSyncProgress(null);
              setSyncOutcome(null);
            }}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 12,
            }}
          >
            dismiss
          </button>
        </div>
      )}

      {/*
        Live sync log — same visual language as DatabaseUpdater on the
        Analytics page (progress-section + logs-container CSS classes are
        defined in src/index.css). Only visible while a Sync now / Copy to
        all run is in flight or has just completed, so the section stays
        compact otherwise.
      */}
      {(syncingId !== null || applyingId !== null || syncLogs.length > 0) && (
        <div style={{ marginBottom: 16 }}>
          <div className="progress-section" style={{ marginBottom: 8 }}>
            <div className="progress-bar" aria-hidden>
              <div
                className={`progress-fill${
                  syncProgress && syncProgress.total > 0 ? '' : ' progress-fill--pulse'
                }`}
                style={{
                  width:
                    syncProgress && syncProgress.total > 0
                      ? `${Math.min(
                          100,
                          Math.round((syncProgress.current / syncProgress.total) * 100)
                        )}%`
                      : '8%',
                }}
              />
            </div>
            <div className="progress-text">
              {syncProgress && syncProgress.total > 0
                ? `${Math.min(
                    100,
                    Math.round((syncProgress.current / syncProgress.total) * 100)
                  )}% — ${syncProgress.current}/${syncProgress.total}`
                : syncingId !== null || applyingId !== null
                  ? 'Working…'
                  : '—'}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--color-text-secondary, #888)',
                textAlign: 'center',
                marginTop: 4,
              }}
            >
              Live progress — logs update in real time. Scroll down to see latest.
            </div>
          </div>
          <div className="logs-section">
            <div className="logs-section-header">
              <h4 style={{ margin: 0, fontSize: 13 }}>Sync log</h4>
              <button
                onClick={() => {
                  setSyncLogs([]);
                  setSyncProgress(null);
                  setSyncOutcome(null);
                }}
                disabled={syncingId !== null || applyingId !== null}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--color-border, #555)',
                  color: 'var(--color-text-secondary, #aaa)',
                  cursor: syncingId !== null || applyingId !== null ? 'not-allowed' : 'pointer',
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                Clear
              </button>
            </div>
            <div className="logs-container" ref={logsContainerRef}>
              {syncLogs.length === 0 ? (
                <div className="log-entry">— No events yet.</div>
              ) : (
                syncLogs.map((line, i) => (
                  <div key={i} className="log-entry">
                    {line}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border, #333)', textAlign: 'left' }}>
            <th style={{ padding: '8px 4px', fontWeight: 500 }}>Field (org)</th>
            <th style={{ padding: '8px 4px', fontWeight: 500 }}>Jira id / kind</th>
            <th
              style={{ padding: '8px 4px', fontWeight: 500, textAlign: 'center' }}
              title={
                productName
                  ? `Toggles whether this field appears in Forecast filters for "${productName}"`
                  : 'Per-product toggle'
              }
            >
              In filters
              {productName ? (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'var(--color-text-secondary, #888)',
                    marginTop: 2,
                  }}
                >
                  for {productName}
                </div>
              ) : null}
            </th>
            <th
              style={{ padding: '8px 4px', fontWeight: 500, textAlign: 'center' }}
              title={
                productName
                  ? `Toggles whether this field appears as a column in Tasks-with-estimation for "${productName}"`
                  : 'Per-product toggle'
              }
            >
              In table
              {productName ? (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'var(--color-text-secondary, #888)',
                    marginTop: 2,
                  }}
                >
                  for {productName}
                </div>
              ) : null}
            </th>
            <th
              style={{ padding: '8px 4px', fontWeight: 500, textAlign: 'center' }}
              title={
                productName
                  ? `Toggles whether this field gets a "By X" breakdown in Forecast summary for "${productName}"`
                  : 'Per-product toggle'
              }
            >
              In summary
              {productName ? (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'var(--color-text-secondary, #888)',
                    marginTop: 2,
                  }}
                >
                  for {productName}
                </div>
              ) : null}
            </th>
            <th
              style={{ padding: '8px 4px', fontWeight: 500, textAlign: 'center' }}
              title={
                productName
                  ? `Toggles whether this field appears in the per-team-member Distribution criteria block under Settings → Team for "${productName}". When on, the Forecast suggest-distribution algorithm honours per-member selected values for this field when matching tasks.`
                  : 'Per-product toggle'
              }
            >
              In distribution
              {productName ? (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'var(--color-text-secondary, #888)',
                    marginTop: 2,
                  }}
                >
                  for {productName}
                </div>
              ) : null}
            </th>
            <th style={{ padding: '8px 4px', fontWeight: 500, width: 110, textAlign: 'right' }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                style={{
                  padding: '14px 4px',
                  fontSize: 13,
                  color: 'var(--color-text-secondary, #888)',
                }}
              >
                No custom fields registered yet. Click <strong>Add custom field</strong> below to
                register one (e.g. Cluster, Squad, Risk level…).
              </td>
            </tr>
          ) : (
            fields.map((cfg) => (
              <tr
                key={cfg.orgFieldId}
                style={{ borderBottom: '1px solid var(--color-border, #333)' }}
              >
                <td style={{ padding: '10px 4px', fontWeight: 500 }}>{cfg.displayName}</td>
                <td
                  style={{
                    padding: '10px 4px',
                    fontSize: 12,
                    color: 'var(--color-text-secondary, #888)',
                  }}
                >
                  <div>{cfg.jiraFieldId}</div>
                  <div style={{ marginTop: 2, opacity: 0.7 }}>{cfg.fieldKind}</div>
                </td>
                <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                  {renderToggle(
                    cfg.inFilters,
                    () => handleToggle(cfg, { inFilters: !cfg.inFilters }),
                    `Toggle ${cfg.displayName} in filters`
                  )}
                </td>
                <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                  {renderToggle(
                    cfg.inTable,
                    () => handleToggle(cfg, { inTable: !cfg.inTable }),
                    `Toggle ${cfg.displayName} in table`
                  )}
                </td>
                <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                  {renderToggle(
                    cfg.inSummary,
                    () => handleToggle(cfg, { inSummary: !cfg.inSummary }),
                    `Toggle ${cfg.displayName} in summary`
                  )}
                </td>
                <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                  {renderToggle(
                    cfg.inDistribution,
                    () => handleToggle(cfg, { inDistribution: !cfg.inDistribution }),
                    `Toggle ${cfg.displayName} in distribution`
                  )}
                </td>
                <td
                  style={{
                    padding: '10px 4px',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <button
                    onClick={() => handleSync(cfg)}
                    disabled={syncingId !== null || applyingId !== null}
                    title={`Pull current Jira values for ${cfg.displayName} for every task in this product`}
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--color-border, #555)',
                      color: 'var(--color-text-secondary, #aaa)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      marginRight: 6,
                      fontSize: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {syncingId === cfg.orgFieldId ? (
                      <Loader2 className="animate-spin" size={12} />
                    ) : (
                      <RefreshCw size={12} />
                    )}
                    Sync now
                  </button>
                  <button
                    onClick={() => handleApplyToAll(cfg)}
                    disabled={syncingId !== null || applyingId !== null}
                    title={`Copy these 4 toggles for ${cfg.displayName} to every product in the organization`}
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--color-border, #555)',
                      color: 'var(--color-text-secondary, #aaa)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      marginRight: 6,
                      fontSize: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {applyingId === cfg.orgFieldId ? (
                      <Loader2 className="animate-spin" size={12} />
                    ) : (
                      <Copy size={12} />
                    )}
                    Copy to all
                  </button>
                  <button
                    onClick={() => handleDelete(cfg)}
                    title="Remove this field from the catalog (org-wide)"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 4,
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {!showAddForm ? (
        <button
          onClick={handleOpenAddForm}
          style={{
            marginTop: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 6,
            border: '1px dashed var(--color-border, #555)',
            background: 'transparent',
            color: 'var(--color-text-secondary, #aaa)',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          <Plus size={14} />
          Add custom field
        </button>
      ) : (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            borderRadius: 8,
            border: '1px solid var(--color-border, #444)',
            background: 'var(--color-bg-elevated, #1e1e2e)',
          }}
        >
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Add custom field</h3>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}>
              Jira field
            </label>
            {loadingJiraFields ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 0',
                  fontSize: 13,
                }}
              >
                <Loader2 className="animate-spin" size={14} />
                Loading Jira fields...
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => setFieldDropdownOpen((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid var(--color-border, #555)',
                    background: 'var(--color-bg, #121212)',
                    cursor: 'pointer',
                    fontSize: 13,
                    minHeight: 38,
                  }}
                >
                  {selectedField ? (
                    <span>
                      {selectedField.name}{' '}
                      <span style={{ color: 'var(--color-text-secondary, #888)', fontSize: 11 }}>
                        ({selectedField.id})
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-text-secondary, #888)' }}>
                      Select a Jira field...
                    </span>
                  )}
                  <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
                </div>
                {fieldDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: 4,
                      borderRadius: 6,
                      border: '1px solid var(--color-border, #555)',
                      background: 'var(--color-bg-elevated, #1a1a2e)',
                      maxHeight: 280,
                      overflow: 'auto',
                      zIndex: 200,
                    }}
                  >
                    <div
                      style={{
                        padding: 8,
                        borderBottom: '1px solid var(--color-border, #333)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <Search size={12} />
                      <input
                        type="text"
                        autoFocus
                        value={fieldSearch}
                        onChange={(e) => setFieldSearch(e.target.value)}
                        placeholder="Search by name or id..."
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: 'var(--color-text)',
                          fontSize: 13,
                        }}
                      />
                    </div>
                    {filteredJiraFields.length === 0 ? (
                      <div
                        style={{
                          padding: 12,
                          fontSize: 12,
                          color: 'var(--color-text-secondary, #888)',
                        }}
                      >
                        No matching fields.
                      </div>
                    ) : (
                      filteredJiraFields.map((f) => (
                        <div
                          key={f.id}
                          onClick={() => {
                            setSelectedField(f);
                            setFieldDropdownOpen(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: 13,
                            borderBottom: '1px solid var(--color-border, #2a2a2a)',
                          }}
                        >
                          {f.name}{' '}
                          <span
                            style={{
                              color: 'var(--color-text-secondary, #888)',
                              fontSize: 11,
                            }}
                          >
                            ({f.id})
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}>
              Field kind
            </label>
            <CustomSelect
              value={pickedKind}
              options={KIND_OPTIONS.map((opt) => ({
                value: opt.value,
                label: `${opt.label} — ${opt.hint}`,
              }))}
              onChange={(val) => setPickedKind(val as CustomFieldKind)}
              style={{ fontSize: 13 }}
            />
          </div>

          <LivePreview
            jiraFieldId={selectedField?.id ?? null}
            selectedKind={pickedKind}
            onRecommendKind={(k) => setPickedKind(k)}
            onLoadingChange={setPreviewLoading}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={handleAddField}
              disabled={!selectedField || saving || previewLoading}
              title={
                previewLoading
                  ? 'Waiting for the live preview to recommend a field kind…'
                  : undefined
              }
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: 'none',
                background: 'var(--color-accent, #6366f1)',
                color: '#fff',
                cursor: !selectedField || saving || previewLoading ? 'not-allowed' : 'pointer',
                opacity: !selectedField || saving || previewLoading ? 0.6 : 1,
                fontSize: 13,
              }}
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={14} /> Saving...
                </>
              ) : previewLoading ? (
                <>
                  <Loader2 className="animate-spin" size={14} /> Previewing…
                </>
              ) : (
                'Register field'
              )}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setSelectedField(null);
              }}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid var(--color-border, #555)',
                background: 'transparent',
                color: 'var(--color-text-secondary, #aaa)',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
